import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TenantService } from '@/cloud/services/tenant.service';
import { TenantLogoComponent } from '../tenant-logo/tenant-logo.component';
import { TranslationService } from '@/layout/service/translation.service';

/**
 * Topbar tenant picker. Multi-tenant users get a dropdown; single-tenant users see the tenant
 * name as a static label. Both sit flush in the topbar: just the tenant's logo (when it has one),
 * its name and, for the dropdown, a chevron. Selecting a tenant is delegated to TenantService,
 * which moves to the new tenant's root.
 */
@Component({
    selector: 'app-tenant-switcher',
    standalone: true,
    imports: [FormsModule, SelectModule, TenantLogoComponent],
    template: `
        @if (tenants.tenants().length === 0) {
            <!-- nothing to show until the tenant list has loaded -->
        } @else if (tenants.isMultiTenant()) {
            <p-select
                class="tenant-switcher__select"
                [options]="tenants.tenants()"
                optionLabel="name"
                optionValue="id"
                [ngModel]="tenants.selectedTenantId()"
                (ngModelChange)="tenants.select($event)"
                [filter]="tenants.tenants().length > 6"
                filterBy="name"
                [placeholder]="t()('tenant.select')"
                appendTo="body"
                [ariaLabel]="t()('tenant.label')"
                [panelStyle]="{ 'max-width': 'calc(100vw - 2rem)' }"
            >
                <ng-template #selectedItem let-tenant>
                    <span class="tenant-switcher__value">
                        <app-tenant-logo [tenantId]="tenant?.id ?? null" [blobId]="tenant?.logoBlobId ?? null" [logoUrl]="tenant?.logoUrl ?? null" [fallback]="false" />
                        <span class="tenant-switcher__name">{{ tenant?.name }}</span>
                    </span>
                </ng-template>
                <ng-template #item let-tenant>
                    <span class="tenant-switcher__option">
                        <app-tenant-logo [tenantId]="tenant.id" [blobId]="tenant.logoBlobId ?? null" [logoUrl]="tenant.logoUrl ?? null" />
                        <span class="tenant-switcher__identity">
                            <span class="tenant-switcher__name">{{ tenant.name }}</span>
                            @if (tenant.key) {
                                <span class="tenant-switcher__caption">{{ tenant.key }}</span>
                            }
                        </span>
                    </span>
                </ng-template>
            </p-select>
        } @else if (tenants.selectedTenant(); as tenant) {
            <span class="tenant-switcher__static" [title]="t()('tenant.label') + ': ' + tenant.name">
                <app-tenant-logo [tenantId]="tenant.id" [blobId]="tenant.logoBlobId ?? null" [logoUrl]="tenant.logoUrl ?? null" [fallback]="false" />
                <span class="tenant-switcher__name">{{ tenant.name }}</span>
            </span>
        }
    `,
    styleUrl: './tenant-switcher.component.scss',
    host: { class: 'tenant-switcher' },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantSwitcherComponent {
    readonly tenants = inject(TenantService);
    readonly t = inject(TranslationService).t;
}
